// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ActionableNotification, IconButton, Tag } from '@carbon/react';
import { ChevronDown, ChevronRight, Close } from '@carbon/icons-react';
import {
  formatAttributeValue,
  getEntityDisplayType,
  type StatusToken,
} from '../../../utils/entityHelpers';
import { EntitySchemas } from '../../../config/entity-schemas';
import { useEquipmentTelemetry } from '../../../hooks/useEquipmentTelemetry';
import { assetName, deriveUnitFullLabel } from '../../../utils/entityLabels';
import { useShellStore } from '../../../stores/shellStore';
import MetricLineChart from '../../common/MetricLineChart';
import { StaleDataNotice } from '../../common/StaleDataNotice';
import { useSiteTime } from '../../../hooks/useSiteTime';
import type { Entity } from '../../../types/digitalTwin';
import type { AssetLiveState } from '../../../utils/assetLiveState';
import type { FlowAdjacency } from '../../../utils/flowRelationships';
import { STATUS_LABELS } from '../../../utils/statusPresentation';
import './AssetDetailPanel.css';

const FIVE_MINS_MS = 5 * 60 * 1000;

export interface AssetDetailPanelProps {
  /** Selected entity id. */
  selectedId: string;
  /**
   * The selected asset's power-flow neighbours, or `undefined` when it has
   * none published.
   *
   * Deliberately just this, rather than the whole diagram model. The panel used
   * to take Asset Topology's `KgModel`, which coupled a shell-level surface to
   * one page's grouping scheme — so replacing that page meant rewriting this.
   * Adjacency plus live state is all it ever read.
   */
  adjacency: FlowAdjacency | undefined;
  live: AssetLiveState;
  entitiesById: Map<string, Entity>;
  /** Needed to query the telemetry-api's observations endpoint for this entity. */
  siteId?: string;
  onClose: () => void;
  /** Navigate the panel and canvas to another node. */
  onSelect: (_id: string) => void;
}

const TAG_TYPE: Record<StatusToken, 'green' | 'magenta' | 'red' | 'gray'> = {
  ok: 'green',
  warn: 'magenta',
  fault: 'red',
  idle: 'gray',
};

/** Attributes already shown in the header, or not worth a row of their own. */
const HEADER_FIELDS = new Set(['label', 'upstreamId', 'hasSOP', 'hasDocument']);

/**
 * Detail panel for the selected asset.
 *
 * Rendered by `AssetDetailPanelHost` above the router, so it survives
 * navigation between site sections. It now lives under `components/shell/`
 * rather than inside a page's folder, which is where it always belonged: the
 * host is shell-level and the panel is not the property of whichever diagram
 * happened to select the asset.
 *
 * The Telemetry section fetches the last 5 minutes of observations from the
 * telemetry-api (via `useEquipmentTelemetry`) rather than reading the
 * digital-twin poll's aggregated state — it's the same source/hook
 * `AlarmTelemetry` uses on the alarm detail page, just a shorter window and
 * finer resolution.
 */
export const AssetDetailPanel = ({
  selectedId,
  adjacency,
  live,
  entitiesById,
  siteId,
  onClose,
  onSelect,
}: AssetDetailPanelProps) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Move focus to the panel when it opens, or when it switches to another
  // asset, so a keyboard user is not left behind on the canvas.
  useEffect(() => {
    headingRef.current?.focus();
  }, [selectedId]);

  const entity = entitiesById.get(selectedId);
  const status: StatusToken = live.statusOf.get(selectedId) ?? 'idle';

  const {
    series: telemetrySeries,
    loading: telemetryLoading,
    error: telemetryError,
    refetch: refetchTelemetry,
  } = useEquipmentTelemetry(siteId, entity?.id, { windowMs: FIVE_MINS_MS, resolution: 'PT1M' });
  const theme = useShellStore(s => s.theme);
  const { format } = useSiteTime();

  const attributeRows = useMemo(() => {
    if (!entity) return [];
    const fields = EntitySchemas[entity.type]?.attributeFields ?? [];
    return fields
      .filter(field => !HEADER_FIELDS.has(field) && entity.attributes?.[field] != null)
      .map(field => ({ field, value: formatAttributeValue(entity.attributes?.[field]) }));
  }, [entity]);

  const updatedAt = entity
    ? (live.stateOf.get(entity.id)?.updatedAt as string | undefined)
    : undefined;

  const title = entity ? deriveUnitFullLabel(entity) : selectedId;
  const label = entity?.attributes?.label?.trim();
  const subtitle = getEntityDisplayType(entity?.type ?? '');

  const toggle = (key: string) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <aside className="kg-panel" aria-labelledby="kg-panel-title">
      <div className="kg-panel__head">
        <div className="kg-panel__title-block">
          {/*
            tabIndex -1 rather than 0: the heading is a focus *target* when the
            panel opens, not something to Tab through afterwards.
          */}
          <h2 id="kg-panel-title" className="kg-panel__title" tabIndex={-1} ref={headingRef}>
            {title}
          </h2>
          <p className="kg-panel__subtitle">{subtitle}</p>
        </div>
        <IconButton label="Close details" kind="ghost" size="sm" align="left" onClick={onClose}>
          <Close size={16} />
        </IconButton>
      </div>

      <div className="kg-panel__status">
        {/* `md`, not `sm`: the panel's type is scaled up and an `sm` pill reads
            undersized beside the heading. */}
        <Tag type={TAG_TYPE[status]} size="md">
          {STATUS_LABELS[status]}
        </Tag>
        {updatedAt && (
          <span className="kg-panel__stamp">Updated {format(updatedAt, 'dateTime')}</span>
        )}
      </div>

      {!entity && (
        <p className="kg-panel__empty">This asset is no longer in the site&apos;s twin payload.</p>
      )}

      {entity && (
        <Section id="entity-info" title="Entity info" collapsed={collapsed} onToggle={toggle}>
          <dl className="kg-panel__rows">
            <Row label="Type" value={getEntityDisplayType(entity.type)} />
            {/*
              The heading names the asset by its upstream id, so the published
              label is a description rather than a name and belongs here with
              the rest of them — see `entityLabels`. Dropped when it says
              nothing the heading has not (`CRAH-2-A` is labelled `CRAH-2-A`).
            */}
            {label && label !== title && <Row label="Description" value={label} />}
            <Row label="Entity ID" value={entity.id} mono />
            {attributeRows.map(row => (
              <Row key={row.field} label={humanizeField(row.field)} value={row.value} />
            ))}
          </dl>
          {attributeRows.length === 0 && (
            <p className="kg-panel__empty">
              {/*
                Only 29 of the ontology's 102 entity types define attribute
                fields — every Brick-style HVAC type has none, so this is the
                expected state for cooling assets rather than an error.
              */}
              No attributes published for this entity type.
            </p>
          )}
        </Section>
      )}

      {entity && (
        <Section
          id="telemetry"
          title="Telemetry · last 5 min"
          collapsed={collapsed}
          onToggle={toggle}
        >
          {telemetryLoading ? (
            <p className="kg-panel__empty">Loading telemetry…</p>
          ) : telemetryError && telemetrySeries.length === 0 ? (
            <ActionableNotification
              kind="error"
              lowContrast
              role="status"
              title="Telemetry unavailable"
              subtitle={telemetryError.message}
              actionButtonLabel="Retry"
              onActionButtonClick={refetchTelemetry}
              hideCloseButton
            />
          ) : telemetrySeries.length === 0 ? (
            <p className="kg-panel__empty">No telemetry published for this asset.</p>
          ) : (
            <>
              {telemetryError && (
                <StaleDataNotice
                  what="telemetry"
                  error={telemetryError}
                  onRetry={refetchTelemetry}
                />
              )}
              {telemetrySeries.map(s => (
                <MetricLineChart key={s.name} series={s} theme={theme} />
              ))}
            </>
          )}
        </Section>
      )}

      {adjacency && (adjacency.in.length > 0 || adjacency.out.length > 0) && (
        <Section id="connections" title="Connections" collapsed={collapsed} onToggle={toggle}>
          <ConnectionList
            heading="Fed by"
            ids={adjacency.in}
            entitiesById={entitiesById}
            onSelect={onSelect}
          />
          <ConnectionList
            heading="Feeds"
            ids={adjacency.out}
            entitiesById={entitiesById}
            onSelect={onSelect}
          />
        </Section>
      )}
    </aside>
  );
};

interface SectionProps {
  id: string;
  title: string;
  collapsed: Record<string, boolean>;
  onToggle: (_id: string) => void;
  children: React.ReactNode;
}

const Section = ({ id, title, collapsed, onToggle, children }: SectionProps) => {
  const isCollapsed = Boolean(collapsed[id]);
  return (
    <section className="kg-panel__section">
      <button
        type="button"
        className="kg-panel__section-toggle"
        aria-expanded={!isCollapsed}
        onClick={() => onToggle(id)}
      >
        {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
        <h3 className="kg-panel__section-title">{title}</h3>
      </button>
      {!isCollapsed && <div className="kg-panel__section-body">{children}</div>}
    </section>
  );
};

const Row = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="kg-panel__row">
    <dt>{label}</dt>
    <dd className={mono ? 'is-mono' : undefined}>{value}</dd>
  </div>
);

const ConnectionList = ({
  heading,
  ids,
  entitiesById,
  onSelect,
}: {
  heading: string;
  ids: string[];
  entitiesById: Map<string, Entity>;
  onSelect: (_id: string) => void;
}) => {
  if (ids.length === 0) return null;
  return (
    <>
      <h4 className="kg-panel__measure-title">
        {heading}
        <span className="kg-panel__count">{ids.length}</span>
      </h4>
      <ul className="kg-panel__links">
        {ids.map(id => (
          <li key={id}>
            <button type="button" className="kg-panel__link" onClick={() => onSelect(id)}>
              {assetName(entitiesById.get(id), id)}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
};

/** "ratedPower" → "Rated power", "serialNumber" → "Serial number". */
const humanizeField = (field: string): string => {
  const spaced = field
    .replace(/([A-Z])/g, ' $1')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};
